import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicePageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:before {
        content: "✓";
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

const ImageSection = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
  }
`;

const DeliveryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const DeliveryCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  text-align: center;

  .icon {
    font-size: 3rem;
    color: var(--secondary-color);
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 20px;
    font-size: 0.9rem;
  }

  .timeline {
    background: var(--secondary-color);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: bold;
    display: inline-block;
  }
`;

const MapSection = styled.div`
  background: #f8f9fa;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 40px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 40px;
`;

const LocationCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;

  h4 {
    color: var(--text-dark);
    margin-bottom: 10px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const CTASection = styled.div`
  background: var(--background-dark);
  color: white;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    margin-bottom: 30px;
    opacity: 0.9;
  }

  .btn {
    background: var(--secondary-color);
    color: white;
    padding: 15px 30px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(245, 115, 58, 0.3);
    }
  }
`;

const NationwideDelivery = () => {
  return (
    <ServicePageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Nationwide Delivery</PageTitle>
          <PageSubtitle>
            Fast, reliable container delivery across all 50 states. Professional
            logistics and experienced drivers ensure your container arrives
            safely and on time.
          </PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <ContentSection>
            <h2>Coast-to-Coast Delivery</h2>
            <p>
              With our extensive logistics network and strategic partnerships,
              we deliver containers to every corner of the United States. From
              major metropolitan areas to remote rural locations, we'll get your
              container where you need it.
            </p>
            <p>
              Our experienced drivers and specialized equipment ensure safe,
              professional delivery with real-time tracking and communication
              throughout the process.
            </p>
            <ul>
              <li>All 50 states coverage</li>
              <li>Urban and rural locations</li>
              <li>Real-time delivery tracking</li>
              <li>Professional delivery crews</li>
              <li>Flexible scheduling options</li>
              <li>Site assessment and preparation guidance</li>
            </ul>
          </ContentSection>

          <ImageSection>
            <img
              src="/assets/2025/08/delivery-truck.jpg"
              alt="Nationwide Delivery"
            />
          </ImageSection>
        </ContentGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Delivery Options
        </h2>

        <DeliveryGrid>
          <DeliveryCard>
            <div className="icon">
              <i className="fas fa-shipping-fast"></i>
            </div>
            <h3>Standard Delivery</h3>
            <p>
              Our most popular delivery option with competitive pricing and
              reliable service to most locations across the country.
            </p>
            <div className="timeline">5-10 Business Days</div>
          </DeliveryCard>

          <DeliveryCard>
            <div className="icon">
              <i className="fas fa-bolt"></i>
            </div>
            <h3>Express Delivery</h3>
            <p>
              Expedited delivery service for urgent requirements with priority
              scheduling and faster transit times.
            </p>
            <div className="timeline">2-5 Business Days</div>
          </DeliveryCard>

          <DeliveryCard>
            <div className="icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <h3>Scheduled Delivery</h3>
            <p>
              Plan your delivery in advance with specific date scheduling to
              coordinate with your project timeline perfectly.
            </p>
            <div className="timeline">Your Choice</div>
          </DeliveryCard>

          <DeliveryCard>
            <div className="icon">
              <i className="fas fa-map-marked-alt"></i>
            </div>
            <h3>Remote Locations</h3>
            <p>
              Specialized delivery service for remote, difficult-to-access
              locations with custom logistics solutions.
            </p>
            <div className="timeline">Custom Timeline</div>
          </DeliveryCard>
        </DeliveryGrid>

        <MapSection>
          <h2>Regional Coverage</h2>
          <p>
            Our strategic locations ensure efficient delivery across the entire
            United States. With headquarters in Indiana and regional partners in
            New Jersey and Utah, we provide comprehensive coverage and
            competitive delivery rates nationwide.
          </p>

          <LocationGrid>
            <LocationCard>
              <h4>Midwest Hub</h4>
              <p>
                Portage, Indiana
                <br />
                Serving Central US
              </p>
            </LocationCard>
            <LocationCard>
              <h4>East Coast</h4>
              <p>
                New Jersey
                <br />
                Serving Eastern Seaboard
              </p>
            </LocationCard>
            <LocationCard>
              <h4>West Coast</h4>
              <p>
                Utah
                <br />
                Serving Western States
              </p>
            </LocationCard>
            <LocationCard>
              <h4>Texas Region</h4>
              <p>
                Partner Network
                <br />
                Serving South/Southwest
              </p>
            </LocationCard>
          </LocationGrid>
        </MapSection>

        <CTASection>
          <h2>Ready to Schedule Delivery?</h2>
          <p>
            Get an instant delivery quote for your location or contact our
            logistics team to discuss special delivery requirements and
            scheduling options.
          </p>
          <Link to="/contact" className="btn">
            Get Delivery Quote
          </Link>
        </CTASection>
      </Container>
    </ServicePageWrapper>
  );
};

export default NationwideDelivery;
